import { CustomModalLayoutProps, Text } from '@wix/design-system';
import { ExportState, FiltersMap } from '@wix/bex-core';
import React, { ReactNode } from 'react';
import { ExportStepConfig } from './ExportStepConfig';
import { ExportStepProgress } from './ExportStepProgress';
import { ExportStepError } from './ExportStepError';
import { ExportStepCancel } from './ExportStepCancel';
import {
  getExportModalErrorProps,
  RenderModalError,
} from './getExportModalErrorProps';

export interface GetExportModalStepProps<T, F extends FiltersMap> {
  state: ExportState<T, F>;
  renderError?: RenderModalError;
  exportModalFooter?: { text: string };
  exportModalTitle?: string;
  onProgressModalTitle?: string;
  exportToCSVMessage?: string;
}

export function getExportModalStepProps<T, F extends FiltersMap>({
  state,
  renderError,
  exportModalFooter,
  exportModalTitle,
  exportToCSVMessage,
  onProgressModalTitle,
}: GetExportModalStepProps<T, F>): Partial<
  CustomModalLayoutProps & { children?: ReactNode; onRequestClose?: () => void }
> {
  const { currentStep, translate: t } = state;

  if (currentStep === 'cancel') {
    return {
      title: t('cairo.export.cancelTheExport'),
      children: <ExportStepCancel state={state} />,
      primaryButtonText: t('cairo.export.cancelTheExportConfirm'),
      secondaryButtonText: t('cairo.export.cancelTheExportGoBack'),
      secondaryButtonProps: { skin: 'destructive' },
      primaryButtonProps: { skin: 'destructive', disabled: false },
      primaryButtonOnClick: () => state.onCancelExportVerifyButtonClick(),
      secondaryButtonOnClick: state.onCancelExportGoBackButtonClick,
      onCloseButtonClick: state.onCancelExportCloseButtonClick,
      onRequestClose: state.onCancelExportCloseButtonClick,
    };
  }

  if (currentStep === 'config') {
    return {
      title: exportModalTitle || t('cairo.export.exportTypeQuestionTitle'),
      children: (
        <ExportStepConfig
          state={state}
          exportToCSVMessage={exportToCSVMessage}
        />
      ),
      primaryButtonText: t('cairo.export.export'),
      primaryButtonOnClick: state.onExportToClick,
      secondaryButtonText: t('cairo.export.cancelButton'),
      secondaryButtonOnClick: state.onCancelClick,
      onCloseButtonClick: state.onCloseClick,
      onRequestClose: state.onCloseClick,
      footnote: exportModalFooter ? (
        <Text dataHook="export-modal-footer-text" size="small">
          {exportModalFooter?.text}
        </Text>
      ) : null,
    };
  }

  if (currentStep === 'error') {
    const modalProps = getExportModalErrorProps({ state, renderError });
    return {
      title: t('cairo.export.exportFailed'),
      secondaryButtonText: t('cairo.export.cancelButton'),
      secondaryButtonOnClick: state.onFailedCancel,
      onCloseButtonClick: state.onFailedClose,
      onRequestClose: state.onFailedClose,
      primaryButtonText: t('cairo.export.tryAgain'),
      ...(modalProps?.primaryButtonProps?.as !== 'a' && {
        primaryButtonOnClick: state.onRetryExportToClick,
      }),
      ...modalProps,
      children: (
        <ExportStepError state={state}>
          {modalProps.children ?? (
            <Text widows>{t('cairo.export.serverErrorMessage')}</Text>
          )}
        </ExportStepError>
      ),
    };
  }

  // exporting
  return {
    title: onProgressModalTitle || t('cairo.export.exportingTitle'),
    children: <ExportStepProgress state={state} />,
    primaryButtonText: t('cairo.export.exporting'),
    primaryButtonProps: {
      disabled: true,
    },
    onCloseButtonClick: state.onExportCloseAttemptClick,
    onRequestClose: state.onExportCloseAttemptClick,
    secondaryButtonText: t('cairo.export.cancelButton'),
    secondaryButtonOnClick: state.onExportSecondaryActionAttempt,
  };
}
